import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useT } from "../../i18n/locale";
import { Icon, type IconName } from "./Icon";
import type { Tone } from "./tone";
import "./toast.css";

// Il tempo che l'avviso resta in vista: abbastanza per leggere una frase; ciò che conta non vive qui
// (l'esito di un'azione si vede anche nella schermata, l'avviso lo conferma).
const TOAST_MS = 6000;

type ToastApi = { show: (message: string, tone?: Tone) => void };
type Shown = { id: number; message: string; tone: Tone };

const ToastContext = createContext<ToastApi | null>(null);

// Il tono aggiunge un'icona oltre al colore: l'avviso dice comunque tutto a parole.
const toneIcons: Partial<Record<Tone, IconName>> = { accent: "info", success: "check", warning: "alert", danger: "alert" };

// Un solo avviso alla volta, in una regione `aria-live="polite"` che esiste da subito e vuota: uno
// screen reader annuncia soltanto ciò che vi compare dentro. Un avviso nuovo prende il posto del
// precedente e riparte il conteggio.
export function ToastProvider({ children }: { children: ReactNode }) {
  const t = useT();
  const [shown, setShown] = useState<Shown | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const counter = useRef(0);

  const show = useCallback((message: string, tone: Tone = "neutral") => {
    clearTimeout(timer.current);
    counter.current += 1;
    setShown({ id: counter.current, message, tone });
    timer.current = setTimeout(() => setShown(null), TOAST_MS);
  }, []);

  useEffect(() => () => clearTimeout(timer.current), []);

  const api = useMemo<ToastApi>(() => ({ show }), [show]);
  const icon = shown ? toneIcons[shown.tone] : undefined;

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="g-toast-region" role="status" aria-live="polite" aria-label={t("common.notifications")}>
        {shown ? (
          // La chiave nuova a ogni avviso fa annunciare anche due messaggi uguali di seguito.
          <div key={shown.id} className="g-toast" data-tone={shown.tone}>
            {icon ? <Icon name={icon} /> : null}
            <span>{shown.message}</span>
          </div>
        ) : null}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const context = useContext(ToastContext);
  if (!context) throw new Error("useToast must be used inside ToastProvider");
  return context;
}
